import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Clock3, LockKeyhole, X } from "lucide-react";
import { useLocation } from "react-router-dom";

import Onboarding from "../pages/Onboarding";
import { StorageKeys } from "../lib/api";
import { storage } from "../lib/storage";
import { useI18n } from "../lib/i18n";

const GRACE_MS = 65 * 60 * 1000;
const FIRST_VISIT_KEY = "gs_first_visit";
const BANNER_SLOT_ID = "gs-onboarding-banner-slot";
const BANNER_ROUTES = new Set(["/", "/settings"]);

const PROFILE_GATE_COPY = {
  en: {
    banner: "Almost there! Make GurSetu yours, Sangat Ji — complete your profile in",
    tap: "Tap for details",
    lockedTitle: "Profile setup is waiting",
    lockedBody:
      "You chose to continue GurSetu without completing your profile. Your profile setup will open automatically when this waiting period finishes.",
    waitPrefix: "Please wait",
    waitSuffix: "before creating your GurSetu profile.",
    keepUsing: "You can keep using GurSetu normally until then.",
    okay: "Okay, I’ll wait",
  },
  hi: {
    banner: "बस एक कदम और! GurSetu को अपना बनाइए, संगत जी — आपकी प्रोफ़ाइल इस समय के बाद बनेगी",
    tap: "जानकारी के लिए टैप करें",
    lockedTitle: "प्रोफ़ाइल सेटअप अभी प्रतीक्षा में है",
    lockedBody:
      "आपने अभी प्रोफ़ाइल पूरी किए बिना GurSetu जारी रखने का विकल्प चुना था। यह प्रतीक्षा समय पूरा होते ही प्रोफ़ाइल सेटअप अपने-आप खुल जाएगा।",
    waitPrefix: "कृपया",
    waitSuffix: "और प्रतीक्षा करें, उसके बाद अपनी GurSetu प्रोफ़ाइल बनाइए।",
    keepUsing: "तब तक आप GurSetu सामान्य रूप से उपयोग कर सकते हैं।",
    okay: "ठीक है",
  },
  pa: {
    banner: "ਬਸ ਇੱਕ ਕਦਮ ਹੋਰ! GurSetu ਨੂੰ ਆਪਣਾ ਬਣਾਓ, ਸੰਗਤ ਜੀ — ਇਸ ਸਮੇਂ ਤੋਂ ਬਾਅਦ ਆਪਣੀ ਪ੍ਰੋਫ਼ਾਈਲ ਪੂਰੀ ਕਰੋ",
    tap: "ਵੇਰਵਾ ਵੇਖਣ ਲਈ ਟੈਪ ਕਰੋ",
    lockedTitle: "ਪ੍ਰੋਫ਼ਾਈਲ ਸੈਟਅੱਪ ਹੁਣੇ ਉਡੀਕ ਵਿੱਚ ਹੈ",
    lockedBody:
      "ਤੁਸੀਂ ਹੁਣੇ ਪ੍ਰੋਫ਼ਾਈਲ ਪੂਰੀ ਕੀਤੇ ਬਿਨਾਂ GurSetu ਵਰਤਣਾ ਜਾਰੀ ਰੱਖਿਆ ਸੀ। ਉਡੀਕ ਦਾ ਸਮਾਂ ਮੁੱਕਦੇ ਹੀ ਪ੍ਰੋਫ਼ਾਈਲ ਸੈਟਅੱਪ ਆਪਣੇ ਆਪ ਖੁੱਲ੍ਹ ਜਾਵੇਗਾ।",
    waitPrefix: "ਕਿਰਪਾ ਕਰਕੇ",
    waitSuffix: "ਹੋਰ ਉਡੀਕ ਕਰੋ, ਫਿਰ ਆਪਣੀ GurSetu ਪ੍ਰੋਫ਼ਾਈਲ ਬਣਾਓ।",
    keepUsing: "ਉਦੋਂ ਤੱਕ ਤੁਸੀਂ GurSetu ਆਮ ਤਰ੍ਹਾਂ ਵਰਤ ਸਕਦੇ ਹੋ।",
    okay: "ਠੀਕ ਹੈ",
  },
};

function formatRemaining(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function unlockDocument() {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  const body = document.body;

  [html, body].forEach((element) => {
    element.style.removeProperty("pointer-events");
    element.style.removeProperty("overflow");
    element.style.removeProperty("touch-action");
  });
}

function ProfileWaitDialog({ copy, remaining, onClose }) {
  const time = formatRemaining(remaining);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={copy.lockedTitle}
      className="fixed inset-0 z-[2147483646] flex items-end justify-center overflow-y-auto bg-black/75 p-3 sm:items-center sm:p-5"
      style={{
        minHeight: "100dvh",
        overscrollBehavior: "none",
        paddingBottom: "max(12px, env(safe-area-inset-bottom))",
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-[26px] border border-amber-300/20 bg-[#0b1119] p-5 text-white shadow-[0_24px_70px_rgba(0,0,0,0.55)] sm:p-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-slate-300 active:scale-95"
        >
          <X size={17} />
        </button>

        <div className="grid h-12 w-12 place-items-center rounded-2xl border border-amber-300/20 bg-amber-300/[0.08] text-amber-300">
          <LockKeyhole size={21} />
        </div>

        <p className="mt-4 text-[9px] font-black uppercase tracking-[2px] text-amber-300">
          GURSETU PROFILE
        </p>

        <h2 className="mt-1 pr-10 text-[22px] font-black leading-7 text-white">
          {copy.lockedTitle}
        </h2>

        <p className="mt-2 text-[12px] leading-5 text-slate-300">{copy.lockedBody}</p>

        <div className="mt-5 flex items-center justify-between gap-3 rounded-[18px] border border-amber-300/20 bg-amber-300/[0.07] px-4 py-3.5">
          <div className="flex min-w-0 items-center gap-2">
            <Clock3 size={17} className="shrink-0 text-amber-300" />
            <span className="truncate text-[11px] font-bold text-slate-300">
              {copy.waitPrefix}
            </span>
          </div>

          <span className="shrink-0 rounded-full border border-amber-300/25 bg-black/20 px-3 py-1.5 font-mono text-[15px] font-black tracking-[1.2px] text-amber-300">
            {time}
          </span>
        </div>

        <p className="mt-3 text-[11px] leading-5 text-slate-400">
          {copy.waitSuffix} {copy.keepUsing}
        </p>

        <button
          type="button"
          onClick={onClose}
          className="mt-5 flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[17px] bg-amber-400 px-4 text-[12px] font-black text-[#171005] active:scale-[0.99]"
        >
          {copy.okay}
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

export default function OnboardingWrapper({ children = null }) {
  const location = useLocation();
  const { lang } = useI18n();

  const copy = PROFILE_GATE_COPY[lang] || PROFILE_GATE_COPY.en;

  const [completed, setCompleted] = useState(() =>
    Boolean(storage.get(StorageKeys.OnboardingComplete, false))
  );

  const [now, setNow] = useState(Date.now());

  const [firstVisit] = useState(() => {
    let time = Number(storage.get(FIRST_VISIT_KEY, 0)) || 0;

    if (!time) {
      time = Date.now();
      storage.set(FIRST_VISIT_KEY, time);
    }

    return time;
  });

  const [slot, setSlot] = useState(null);
  const [waitOpen, setWaitOpen] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    let element = document.getElementById(BANNER_SLOT_ID);
    let createdHere = false;

    if (!element) {
      element = document.createElement("div");
      element.id = BANNER_SLOT_ID;
      element.style.width = "100%";
      element.style.position = "relative";
      element.style.zIndex = "50";
      document.body.insertBefore(element, document.body.firstChild);
      createdHere = true;
    }

    setSlot(element);

    return () => {
      setSlot(null);
      if (createdHere && element?.parentNode) element.remove();
    };
  }, []);

  useEffect(() => {
    const syncStatus = () => {
      const done = Boolean(storage.get(StorageKeys.OnboardingComplete, false));

      if (done) {
        setCompleted(true);
        setWaitOpen(false);
        unlockDocument();
        return;
      }

      setCompleted(false);
      setNow(Date.now());
    };

    syncStatus();

    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    const handleStorage = () => syncStatus();
    const handleComplete = () => {
      setCompleted(true);
      setWaitOpen(false);
      unlockDocument();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") syncStatus();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("gursetu:onboarding-complete", handleComplete);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("gursetu:onboarding-complete", handleComplete);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    const openWaitMessage = () => {
      if (!completed) setWaitOpen(true);
    };

    window.addEventListener("gursetu:open-profile-wait", openWaitMessage);
    return () =>
      window.removeEventListener("gursetu:open-profile-wait", openWaitMessage);
  }, [completed]);

  useEffect(() => {
    if (completed) unlockDocument();
  }, [completed]);

  const remaining = Math.max(0, GRACE_MS - (now - firstVisit));
  const locked = !completed && remaining <= 0;
  const routeAllowsBanner = BANNER_ROUTES.has(location.pathname);
  const showBanner = !completed && routeAllowsBanner && remaining > 0;

  useEffect(() => {
    if (remaining <= 0) setWaitOpen(false);
  }, [remaining]);

  const banner = useMemo(() => {
    if (!showBanner) return null;

    return (
      <button
        type="button"
        onClick={() => setWaitOpen(true)}
        data-testid="onboarding-banner"
        className="block w-full overflow-hidden text-white lg:ml-[280px] lg:w-[calc(100%_-_280px)]"
        style={{
          background:
            "linear-gradient(90deg,#7a0c10,#b82028,#d84248,#ef665b,#d84248,#b82028,#7a0c10)",
        }}
      >
        <div className="mx-auto flex min-h-[44px] w-full max-w-5xl flex-wrap items-center justify-center gap-x-2 gap-y-1 px-3 py-2 text-center text-[11px] font-semibold sm:text-[13px]">
          <span aria-hidden="true">🙏</span>
          <span className="min-w-0">{copy.banner}</span>
          <span className="shrink-0 rounded-full border border-white/30 bg-white/15 px-2.5 py-0.5 font-mono font-black tabular-nums tracking-[1.1px]">
            {formatRemaining(remaining)}
          </span>
          <span className="shrink-0 text-[9px] font-bold text-white/80 underline underline-offset-2">
            {copy.tap}
          </span>
        </div>
      </button>
    );
  }, [copy.banner, copy.tap, remaining, showBanner]);

  return (
    <>
      {children}

      {slot && banner && createPortal(banner, slot)}

      {typeof document !== "undefined" &&
        waitOpen &&
        !completed &&
        remaining > 0 &&
        createPortal(
          <ProfileWaitDialog
            copy={copy}
            remaining={remaining}
            onClose={() => setWaitOpen(false)}
          />,
          document.body
        )}

      {typeof document !== "undefined" &&
        locked &&
        createPortal(
          <div
            className="fixed inset-0 z-[2147483647] overflow-x-hidden overflow-y-auto bg-[#050810]"
            style={{
              minHeight: "100dvh",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "none",
              touchAction: "pan-y",
            }}
          >
            <Onboarding />
          </div>,
          document.body
        )}
    </>
  );
}
